/**
 * ADA fix (TGB-E361): announce dynamically updated review filter results
 * to screen readers when star-filter buttons change the review count.
 * WCAG 4.1.3 — the live region must exist on page load; content injected
 * into it later is announced by screen readers.
 */
(function patchOkendoFilterLiveRegion() {
  const liveRegion = document.createElement('div');
  liveRegion.setAttribute('aria-live', 'polite');
  liveRegion.setAttribute('aria-atomic', 'true');
  liveRegion.className = 'visually-hidden';
  document.body.appendChild(liveRegion);

  let countObserver = null;

  function attachCountObserver() {
    const countEl = document.querySelector('.oke-w-reviews-count');
    if (!countEl || countObserver) return;

    countObserver = new MutationObserver(() => {
      const text = countEl.textContent.trim();
      // Clear first so screen readers re-announce even when text is identical.
      liveRegion.textContent = '';
      requestAnimationFrame(() => {
        liveRegion.textContent = text;
      });
    });

    countObserver.observe(countEl, { childList: true, subtree: true, characterData: true });
    domObserver.disconnect();
  }

  const domObserver = new MutationObserver(attachCountObserver);
  domObserver.observe(document.body, { childList: true, subtree: true });
  attachCountObserver();
})();

/**
 * ADA fix (TGB-E362): move keyboard focus to newly revealed review body
 * text when a "Read more" button is activated in the Okendo reviews widget.
 * WCAG 2.4.3 — Okendo removes `oke-is-truncated` but does not manage focus.
 *
 * We use a MutationObserver on the review container so focus moves only
 * after Okendo finishes its own DOM and focus updates, which is more
 * reliable than a fixed requestAnimationFrame.
 */
document.addEventListener(
  'click',
  (event) => {
    const btn = event.target.closest('.oke-reviewContent-readMore');
    if (!btn) return;

    const reviewContent = btn.closest('.oke-reviewContent');
    if (!reviewContent || !reviewContent.classList.contains('oke-is-truncated')) return;

    const classObserver = new MutationObserver((_mutations, obs) => {
      if (reviewContent.classList.contains('oke-is-truncated')) return;
      obs.disconnect();

      const body = reviewContent.querySelector('.oke-reviewContent-body');
      if (!body) return;

      if (!body.hasAttribute('tabindex')) {
        body.setAttribute('tabindex', '-1');
      }
      // Defer one more frame to let Okendo settle any final focus changes.
      requestAnimationFrame(() => body.focus({ preventScroll: false }));
    });

    classObserver.observe(reviewContent, { attributes: true, attributeFilter: ['class'] });
    // Safety: disconnect if class never changes (e.g. Okendo error).
    setTimeout(() => classObserver.disconnect(), 2000);
  },
  true // capture phase: fires before Okendo's handler
);

/**
 * ADA fix: make the Okendo media carousel's Prev/Next buttons and individual
 * photo buttons keyboard-accessible.
 *
 * Okendo sets tabindex="-1" on `.oke-mediaCarousel-prev`,
 * `.oke-mediaCarousel-next`, and `.oke-media-link` elements inside the
 * carousel, removing them from the tab order entirely (WCAG 2.1.1).
 * The Prev/Next buttons also lack aria-label (WCAG 4.1.2).
 *
 * We use a MutationObserver so the patch runs both on initial widget render
 * and whenever Okendo updates the carousel (e.g. after pagination).
 */
(function patchOkendoCarouselA11y() {
  function patch() {
    // Restore prev/next buttons to the tab order and label them.
    document.querySelectorAll('.oke-mediaCarousel-prev, .oke-mediaCarousel-next').forEach((btn) => {
      if (btn.getAttribute('tabindex') === '-1') {
        btn.setAttribute('tabindex', '0');
      }
      if (!btn.getAttribute('aria-label')) {
        btn.setAttribute('aria-label', btn.classList.contains('oke-mediaCarousel-prev') ? 'Previous' : 'Next');
      }
    });

    // Restore individual carousel photo buttons to the tab order.
    document.querySelectorAll('.oke-mediaCarousel .oke-media-link').forEach((btn) => {
      if (btn.getAttribute('tabindex') === '-1') {
        btn.setAttribute('tabindex', '0');
      }
    });
  }

  patch();

  const observer = new MutationObserver(patch);
  observer.observe(document.body, { childList: true, subtree: true });

  // When a carousel element receives focus, scroll it clear of the sticky header.
  document.addEventListener('focusin', (event) => {
    const el = event.target.closest(
      '.oke-mediaCarousel-prev, .oke-mediaCarousel-next, .oke-mediaCarousel .oke-media-link'
    );
    if (!el) return;

    // Run after the browser's own scroll-to-focus so we can measure and correct.
    requestAnimationFrame(() => {
      const headerSection = document.querySelector('.header-section');
      if (!headerSection) return;

      const headerHeight = headerSection.getBoundingClientRect().height;
      const elTop = el.getBoundingClientRect().top;

      if (elTop < headerHeight) {
        window.scrollBy({ top: elTop - headerHeight - 50, behavior: 'smooth' });
      }
    });
  });
})();
